import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useRequest } from 'shared/hooks';
import {
  createNotes, deleteNote, getEntityNotes, updateNote,
} from 'shared/api/notes';
import DataDisplay from 'shared/components/data-display/DataDisplay';
import NotesDialog from './NotesDialog';

const sortedNotes = (notes) => notes.toSorted((n1, n2) => {
  if (n1.note_type.position === null) return 1;
  if (n2.note_type.position === null) return -1;
  return n1.note_type.position - n2.note_type.position;
});

/**
 * @param {Object} props - Component props
 * @param {string} props.entityType - One of: 'Building', 'Unit', 'Tenant', 'Complex',
 * 'Property Manager'
 * @param {number} props.entityID - ID of the entity
 * @param {boolean} [props.isEditing=false] - Whether the notes dialog is open
 * @param {Function} [props.onStopEditing] - Callback when editing stops
 * @param {string} [props.title='Edit Notes'] - Title for the notes dialog
 * @param {string} [props.legacyNotes=''] - Legacy notes text
 * @param {Object} [props.entityConfig] - Configuration object for entity updates
 * @param {string} [props.helperText='Click the edit to get started'] - Helper text displayed when
 * no notes exist
 * @returns {JSX.Element}
 */
export default function NotesManager({
  entityType,
  entityID,
  isEditing = false,
  onStopEditing = () => {},
  title = 'Edit Notes',
  legacyNotes = '',
  entityConfig,
  helperText = 'Click the edit to get started',
}) {
  const [notes, setNotes] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [creatorDirty, setCreatorDirty] = useState(false);
  const { request } = useRequest();

  useEffect(() => {
    request(getEntityNotes(entityType, entityID))
      .then((res) => setNotes(res?.rows ? sortedNotes(res?.rows) : []))
      .catch((err) => console.error('error loading note types', err));
  }, [request, entityID, entityType]);

  useEffect(() => {
    setDirty(creatorDirty || editorDirty);
  }, [creatorDirty, editorDirty]);

  const handleAddNotes = (newNotes, afterSave, dispatchSaved, dispatchParams) => {
    request(createNotes(newNotes), `Created ${newNotes.length} note(s)`)
      .then((res) => {
        if (!res) return;
        setNotes(sortedNotes([...notes].concat(res)));
        afterSave();
        dispatchSaved(dispatchParams);
      });
  };

  const handleUpdateNote = (note) => {
    request(updateNote(note), 'Updated note')
      .then((updatedNote) => {
        const tmp = [...notes];
        const i = tmp.findIndex((n) => note.id === n.id);
        if (i === -1) {
          console.error('failed to update note');
        }
        tmp[i] = updatedNote;
        setNotes(tmp);
      });
  };

  const handleDeleteNote = (id) => {
    request(deleteNote(id), 'Deleted note')
      .then(() => {
        const tmp = [...notes];
        const i = tmp.findIndex((n) => n.id === id);
        if (i === -1) {
          console.error('failed to delete note');
          return;
        }
        tmp.splice(i, 1);
        setNotes(tmp);
      });
  };

  const handleDeleteLegacyNotes = () => {
    const {
      body, update, key = 'notes', dispatch,
    } = entityConfig;
    request(update(body.id, { building_id: body.building_id, [key]: '' }), 'Removed legacy notes')
      .then((res) => {
        dispatch({ action: 'set', payload: res });
      });
  };

  return (
    // The legacy notes render as a sibling Grid container below, and the margins collapse.
    // Space the seam here; wrapping both in a flex context stretches the note boxes.
    <Grid container spacing={1} sx={legacyNotes ? { mb: 1 } : undefined}>
      <NotesList notes={notes} helperText={helperText} />
      <NotesDialog
        initialNotes={notes}
        open={isEditing}
        entityType={entityType}
        entityID={entityID}
        onClose={onStopEditing}
        onAddNotes={handleAddNotes}
        onUpdateNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
        dirty={dirty}
        setCreatorDirty={setCreatorDirty}
        setEditorDirty={setEditorDirty}
        title={title}
        legacyNotes={legacyNotes}
        onDeleteLegacyNotes={handleDeleteLegacyNotes}
      />
    </Grid>
  );
}

/**
 * @param {Object} props - Component props
 * @param {Array<Object>} props.notes - Array of note objects
 * @param {string} props.helperText - Text to display when no notes exist
 * @returns {JSX.Element}
 */
export function NotesList({ notes, helperText }) {
  if (notes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 40,
        }}
      >
        <Typography
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          {helperText}
        </Typography>
      </Box>
    );
  }
  return notes.map((note) => (
    <DataDisplay
      key={note.id}
      label={note.note_type.name}
      value={note.value}
      copyable
      multiline
    />
  ));
}
